import React, { useState, useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@apollo/client'
import { Map } from 'immutable'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider } from '../../../../StaticText/StaticTextContext'
import { getBoundActions } from '../../../../../actions/index'
import { selectAuth } from '../../../../../services/auth/selectors'
import useCommunityEnabled from '../../../../../services/community/shared/useCommunityEnabled'
import {
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../../services/apollo-graphql/index'
import {
  GET_CURRENT_PROFILE,
  GET_CANONICAL_CENTERS,
} from '../../../../../services/community/query'
import ErrorPage from '../../../../ErrorPage/index'
import { ERROR_TYPE_404 } from '../../../../ErrorPage/types'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import CommunityProfileWelcome from '../CommunityProfileWelcome/index'
import type {
  GetCurrentProfileResponse,
  GetCanonicalCentersResponse,
  StaticTextMap,
} from '../../../../../services/community/types'
import usePrimaryAppLanguage from '../../../../../services/community/shared/usePrimaryAppLanguage'
import useRequireAuth from '../../../../../services/community/shared/useRequireAuth'
import {
  MobileBreakpointProvider,
  COMMUNITY_BREAKPOINTS,
} from '../../../../../services/community/shared/MobileBreakpointContext'

import CommunityProfileSettingsForm from './CommunityProfileSettingsForm'

export interface CommunityProfileSettingsPageProps {
  history: { push: (path: string) => void; goBack: () => void }
  staticText: StaticTextMap
}

/**
 * Route shell for /profile/edit.
 *
 * Owns the feature flag + auth gate, the two data-fetching queries, and
 * the one-time welcome overlay. Mounts the form only after profile and
 * canonical-center queries have both settled, so the form's useReducer
 * initializer sees fully-ready inputs and paints populated on render 1
 * when data is cached. The `key={profile.uid}` on the form ensures a
 * remount on account switch but leaves the form mounted across background
 * cache-and-network refetches, preserving in-flight user edits.
 */
function CommunityProfileSettingsPage ({
  history,
  staticText,
}: CommunityProfileSettingsPageProps) {
  const { enabled: isCommunityProfileEnabled } = useCommunityEnabled()

  const auth = useSelector(selectAuth)
  const user = useSelector((state: { user?: Map<string, unknown> }) => state.user)
  const dispatch = useDispatch()
  const actions = getBoundActions(dispatch)

  const loggedInUid = auth.get('uid')
  const isLoggedIn = !!loggedInUid
  const jwt = auth.get('jwt')
  const language = usePrimaryAppLanguage(user)

  useRequireAuth()

  // --- Welcome overlay ---
  const communityProfileWelcomeMessage = useSelector(
    (state: { featureTracking?: Map<string, unknown> }) => {
      if (!state.featureTracking || !Map.isMap(state.featureTracking)) return null
      return state.featureTracking.getIn(['data', 'communityProfileWelcomeMessage'], null)
    },
  )
  const [welcomeDismissedSession, setWelcomeDismissedSession] = useState(false)
  const showWelcome = isLoggedIn
    && !welcomeDismissedSession
    && !communityProfileWelcomeMessage

  const handleWelcomeDismiss = useCallback((permanent: boolean) => {
    setWelcomeDismissedSession(true)
    if (permanent) {
      actions.featureTracking.setFeatureTrackingDataPersistent({
        data: Map({
          communityProfileWelcomeMessage: true,
        }),
      })
    }
  }, [actions])

  // Apollo: profile data
  const { data: profileData, loading: profileLoading } = useQuery<GetCurrentProfileResponse>(
    GET_CURRENT_PROFILE,
    {
      // Settings page renders identity fields only — no in-progress / watch
      // history carousel and no system-playlist sharing rows. Skip the heavy
      // activity selection so we don't pay for the nested content payload.
      // includeWatchHistory = includeMemberFields && includeProfileActivity.
      variables: {
        language,
        contentLanguage: [language],
        includeMemberFields: true,
        includeProfileActivity: false,
        includeWatchHistory: false,
      },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      skip: !isLoggedIn,
      context: { jwt },
    },
  )
  const profile = profileData?.userProfileCurrent ?? null

  // Apollo: canonical center tags. The backend returns localized `title`s
  // when `language` is supplied — mirror the pattern used by GET_PLAYLIST_BY_ID
  // (primary app language first, English fallback for any missing translation).
  const {
    data: canonicalCentersData,
    loading: canonicalCentersLoading,
    error: canonicalCentersError,
  } = useQuery<GetCanonicalCentersResponse>(
    GET_CANONICAL_CENTERS,
    {
      variables: { language: [language, 'en'] },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      skip: !isLoggedIn,
      context: { jwt },
    },
  )

  const availableTopicsJS = useMemo(() => {
    const raw = canonicalCentersData?.contentList?.content || []
    return raw
      .filter((row) => row?.contentId != null)
      .map((row) => ({
        id: row.contentId as number,
        label: row.title || '',
      }))
  }, [canonicalCentersData])

  // --- Feature flag gate ---
  if (!isCommunityProfileEnabled) {
    return <ErrorPage history={history} code={ERROR_TYPE_404} />
  }

  // --- Auth gate ---
  if (!isLoggedIn) {
    return (
      <div className="community-profile-settings community-profile-settings--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  // --- Initial data gate ---
  // Gate only blocks on the first visit when neither query has cached data.
  // On revisit, cache-and-network returns cached `profile` / tag options
  // synchronously even while `loading` flips true for the background refetch,
  // so we mount the form immediately and let the refetch update the Apollo
  // cache transparently. Matches the pattern in CommunityMyLibraryPage /
  // CommunityProfilePage.
  const profileBlocked = profileLoading && !profile
  const tagsBlocked = canonicalCentersLoading
    && availableTopicsJS.length === 0
    && !canonicalCentersError
  if (profileBlocked || tagsBlocked) {
    return (
      <div className="community-profile-settings community-profile-settings--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  return (
    <MobileBreakpointProvider breakpoints={COMMUNITY_BREAKPOINTS}>
      <StaticTextProvider staticText={staticText}>
        {showWelcome && (
          <CommunityProfileWelcome onDismiss={handleWelcomeDismiss} />
        )}
        <CommunityProfileSettingsForm
          key={profile?.uid ?? 'new-user'}
          profile={profile}
          availableTopicsJS={availableTopicsJS}
          language={language}
          history={history}
        />
      </StaticTextProvider>
    </MobileBreakpointProvider>
  )
}

// TODO: Deduplicate picker strings (pickerTitle, pickerDescription, pickerCount,
// pickerMaxReached, pickerNoPlaylists, pickerItemCount, addPlaylists, editPlaylists)
// — duplicated in communityProfileUserView lang files.
// TODO: Deduplicate journey stage labels (journeyStage_*, journey*Desc) — duplicated
// in communityProfileMemberView and communityProfileUserView lang files.
export default connectStaticText({ storeKey: 'communityProfileSettingsPage' })(CommunityProfileSettingsPage)
